/**
 * Mirror-axis picking, shared by the construction Mirror sub-tool (#180) and
 * the leather Mirror sub-tool (#973).
 *
 * Both gestures answer the same two questions against a set of outlines:
 *
 *   1. "which anchor / segment is the cursor closest to?" — `findAxisCandidate`
 *   2. "what reflection does that ref describe?"          — `resolveAxisSpecFrom`
 *
 * The construction version answered them against construction lines only, with
 * the geometry inlined in `MirrorController` / `constructionMirror`. #973 needs
 * the identical scan over part and cutout outlines, so the geometry moved here
 * and both callers drive it with their own candidate list. What differs between
 * the two — which outlines are eligible, and what a commit produces — stays with
 * each caller; this module is the part that must not fork (#990).
 *
 * A candidate ref addresses geometry by `(key, pathIndex, segmentIndex)`, where
 * `key` is whatever opaque id the caller used to label the outline it passed in
 * (a construction line id, or a leather `piece:<id>` / `cutout:<id>` tag).
 */
import type paper from "paper";
import { applyMirror, buildMirrorMatrix } from "./constructionMirror";
import type { AxisSpec } from "./constructionMirror";
import {
  currentPointerKind,
  hitRadiusFor,
} from "../canvas/pointerPrecision";

/** One outline offered to the scan, labelled with the caller's own id. */
export interface AxisOutline {
  key: string;
  pathData: string;
}

/** A picked axis: an anchor to invert through (`point`) or a straight segment
 *  to reflect across (`segment`), addressed within one outline. */
export interface AxisRef {
  kind: "segment" | "point";
  key: string;
  pathIndex: number;
  segmentIndex: number;
}

/** Hit radii, in screen px so they feel the same at any zoom. Anchors get the
 *  larger radius AND win ties (see `findAxisCandidate`). */
const ANCHOR_RADIUS_PX = 10;
const SEGMENT_RADIUS_PX = 6;

/** Split a parsed CompoundPath into its subpaths — a lone path reports no
 *  children, so it stands as its own single subpath. */
function subpathsOf(cp: paper.CompoundPath): paper.Path[] {
  return cp.children && cp.children.length > 0
    ? (cp.children as paper.Path[])
    : [cp as unknown as paper.Path];
}

/**
 * Find the anchor or straight segment nearest `cursor` across `outlines`.
 *
 * Anchors win over segments when both qualify, so a cursor near a corner locks
 * onto the point-mirror gesture rather than reflecting across one of the two
 * edges meeting there — the corner is the more precise thing to have aimed at.
 *
 * CURVED segments are skipped (issue #340): a segment-axis is the chord between
 * two adjacent anchors, and for a curve (an ellipse's quadrant arc, a bezier
 * span) that chord is a diagonal with nothing to do with the arc under the
 * cursor. Anchor pivots stay available on curved outlines.
 *
 * Returns null when nothing is within the hit radius.
 */
export function findAxisCandidate(
  outlines: ReadonlyArray<AxisOutline>,
  cursor: { x: number; y: number },
  scope: paper.PaperScope,
): AxisRef | null {
  if (outlines.length === 0) return null;
  const zoom = scope.view.zoom > 0 ? scope.view.zoom : 1;
  // A finger grabs both wider (#1150), keeping the anchors' lead.
  const pointer = currentPointerKind();
  const anchorRMm = hitRadiusFor(ANCHOR_RADIUS_PX, pointer) / zoom;
  const segmentRMm = hitRadiusFor(SEGMENT_RADIUS_PX, pointer) / zoom;
  const cursorPt = new scope.Point(cursor.x, cursor.y);
  let bestAnchor: { ref: AxisRef; distSq: number } | null = null;
  let bestSegment: { ref: AxisRef; distSq: number } | null = null;

  for (const outline of outlines) {
    if (!outline.pathData) continue;
    let cp: paper.CompoundPath | null = null;
    try {
      cp = new scope.CompoundPath({
        pathData: outline.pathData,
        insert: false,
      });
    } catch {
      continue;
    }
    const subs = subpathsOf(cp);
    for (let pi = 0; pi < subs.length; pi++) {
      const sub = subs[pi]!;
      const segs = sub.segments;
      for (let si = 0; si < segs.length; si++) {
        const seg = segs[si]!;
        const dx = seg.point.x - cursor.x;
        const dy = seg.point.y - cursor.y;
        const dSq = dx * dx + dy * dy;
        if (dSq <= anchorRMm * anchorRMm) {
          if (!bestAnchor || dSq < bestAnchor.distSq) {
            bestAnchor = {
              ref: {
                kind: "point",
                key: outline.key,
                pathIndex: pi,
                segmentIndex: si,
              },
              distSq: dSq,
            };
          }
        }
      }
      // Per-segment distance: `getNearestLocation` on the whole subpath gives
      // the closest point but not which segment owns it, and we need the
      // specific segment to build the ref — so walk the curves.
      for (let ci = 0; ci < sub.curves.length; ci++) {
        const curve = sub.curves[ci]!;
        if (!curve.isStraight()) continue;
        const loc = curve.getNearestLocation(cursorPt);
        if (!loc) continue;
        const dx = loc.point.x - cursor.x;
        const dy = loc.point.y - cursor.y;
        const dSq = dx * dx + dy * dy;
        if (dSq <= segmentRMm * segmentRMm) {
          if (!bestSegment || dSq < bestSegment.distSq) {
            bestSegment = {
              ref: {
                kind: "segment",
                key: outline.key,
                pathIndex: pi,
                segmentIndex: ci,
              },
              distSq: dSq,
            };
          }
        }
      }
    }
    cp.remove();
  }
  if (bestAnchor) return bestAnchor.ref;
  if (bestSegment) return bestSegment.ref;
  return null;
}

/**
 * Resolve a ref against the outline geometry it addresses, yielding the
 * absolute reflection it describes. Returns null when the sub-path or anchor
 * is gone (the caller treats that as "this axis no longer exists").
 */
export function resolveAxisSpecFrom(
  pathData: string,
  ref: { kind: "segment" | "point"; pathIndex: number; segmentIndex: number },
  scope: paper.PaperScope,
): AxisSpec | null {
  if (!pathData) return null;
  let cp: paper.CompoundPath | null = null;
  try {
    cp = new scope.CompoundPath({ pathData, insert: false });
  } catch {
    return null;
  }
  const sub = subpathsOf(cp)[ref.pathIndex];
  if (!sub) {
    cp.remove();
    return null;
  }
  const segs = sub.segments;
  const start = segs[ref.segmentIndex];
  if (!start) {
    cp.remove();
    return null;
  }
  const startPt = { x: start.point.x, y: start.point.y };
  if (ref.kind === "point") {
    cp.remove();
    return { kind: "point", origin: startPt };
  }
  // segment: the far endpoint is the next anchor, wrapping to anchor 0 when the
  // addressed anchor is the last one on a closed subpath.
  let endIdx = ref.segmentIndex + 1;
  if (endIdx >= segs.length) {
    if (sub.closed) endIdx = 0;
    else {
      cp.remove();
      return null;
    }
  }
  const end = segs[endIdx]!;
  // Refuse a segment-axis whose endpoints carry handles pointing into it: the
  // chord between two anchors has nothing to do with the visible arc, and
  // reflecting across that chord is the diagonal-axis surprise #340 fixed for
  // fresh candidates (#371 covers axes that turned curved after the fact).
  if (!start.handleOut.isZero() || !end.handleIn.isZero()) {
    cp.remove();
    return null;
  }
  const endPt = { x: end.point.x, y: end.point.y };
  cp.remove();
  return {
    kind: "segment",
    origin: startPt,
    dir: { x: endPt.x - startPt.x, y: endPt.y - startPt.y },
  };
}

/** Distance along a subpath from its start to anchor `index` — the sum of the
 *  curves before it. Exact by construction, unlike a nearest-point search,
 *  which on a closed symmetric outline can answer with the wrong lobe. */
function anchorOffset(sub: paper.Path, index: number): number {
  let acc = 0;
  for (let i = 0; i < index; i++) {
    const c = sub.curves?.[i];
    if (!c) return acc;
    acc += c.length;
  }
  return acc;
}

/** Where anchor `index` of `modelSub` sits on `liveSub` — the same anchor,
 *  after whatever the in-flight drag has done to the line. */
function liveAnchorPoint(
  modelSub: paper.Path,
  liveSub: paper.Path,
  index: number,
): { x: number; y: number } | null {
  const model = modelSub.segments?.[index];
  if (!model) return null;
  // Same anchor count means the live path is the model path transformed, so
  // index N is still anchor N. Exact under translation, rotation AND
  // non-uniform scaling — none of which preserve arc-length fractions.
  if (liveSub.segments?.length === modelSub.segments?.length) {
    const live = liveSub.segments[index];
    return live ? { x: live.point.x, y: live.point.y } : null;
  }
  // Different anchor counts: the line was REBUILT rather than transformed, so
  // there is no index to follow. Both paths still trace the same outline from
  // the same start in the same direction, so the fraction of the way round is
  // the anchor's identity — a circle's rightmost point is halfway along
  // whether the path spends 4 anchors getting there or 8.
  const mLen = modelSub.length;
  const lLen = liveSub.length;
  if (!(mLen > 0) || !(lLen > 0)) return null;
  const pt = liveSub.getPointAt((anchorOffset(modelSub, index) / mLen) * lLen);
  return pt ? { x: pt.x, y: pt.y } : null;
}

/**
 * Resolve a ref against a line whose rendered geometry is mid-drag, yielding
 * the reflection the COMMIT will describe once the drag lands.
 *
 * A ref addresses geometry by anchor index, and only the model geometry says
 * which feature a given index names — that is what the commit-time
 * `resolveAxisSpecFrom` reads. A live drag re-derives derived lines every
 * frame, and a re-derive can hand back the same outline built from a different
 * number of anchors: an ellipse primitive's offset commits as a clean 4-anchor
 * ellipse but previews as paperjs-offset's 8-anchor one. Read the index
 * straight off THAT and `segmentIndex: 2` silently changes which anchor it
 * means — the copy previewed square above its source, then snapped to the
 * right on release (#343).
 *
 * So the model picks the feature and the live path only says where it is now.
 */
export function resolveAxisSpecOnLive(
  modelPathData: string,
  livePathData: string,
  ref: { kind: "segment" | "point"; pathIndex: number; segmentIndex: number },
  scope: paper.PaperScope,
): AxisSpec | null {
  // The model decides both WHICH feature this is and whether it is still a
  // legal axis at all (the #340 / #371 curved-segment refusal lives in here).
  const modelSpec = resolveAxisSpecFrom(modelPathData, ref, scope);
  if (!modelSpec) return null;
  if (!livePathData || livePathData === modelPathData) return modelSpec;
  let modelCp: paper.CompoundPath | null = null;
  let liveCp: paper.CompoundPath | null = null;
  try {
    modelCp = new scope.CompoundPath({ pathData: modelPathData, insert: false });
    liveCp = new scope.CompoundPath({ pathData: livePathData, insert: false });
  } catch {
    modelCp?.remove();
    liveCp?.remove();
    return modelSpec;
  }
  try {
    const modelSub = subpathsOf(modelCp)[ref.pathIndex];
    const liveSub = subpathsOf(liveCp)[ref.pathIndex];
    // No live counterpart — keep the model's answer rather than inventing one.
    if (!modelSub || !liveSub) return modelSpec;
    const origin = liveAnchorPoint(modelSub, liveSub, ref.segmentIndex);
    if (!origin) return modelSpec;
    if (ref.kind === "point") return { kind: "point", origin };
    // A segment axis needs its far end mapped the same way. `resolveAxisSpecFrom`
    // already established the far anchor exists (wrapping on a closed subpath),
    // so mirror that wrap here.
    let endIdx = ref.segmentIndex + 1;
    if (endIdx >= modelSub.segments.length) {
      if (!modelSub.closed) return modelSpec;
      endIdx = 0;
    }
    const end = liveAnchorPoint(modelSub, liveSub, endIdx);
    if (!end) return modelSpec;
    return {
      kind: "segment",
      origin,
      dir: { x: end.x - origin.x, y: end.y - origin.y },
    };
  } finally {
    modelCp.remove();
    liveCp.remove();
  }
}

/**
 * The nearest axis feature — anchor OR straight segment — across `outlines`,
 * with NO hit radius: however far away the cursor is, something wins (or null
 * when the outlines hold neither).
 *
 * This is the "mirror that way" fallback (#973): with an object selected and
 * the cursor out in open space, the nearest feature of that object is the axis
 * the user means, so pointing past a side is enough to say which way to
 * reflect. Straight-out from an edge, that edge's nearest point beats both its
 * corners and you get a reflection; diagonally past a corner, the corner wins
 * and you get the 180° point mirror — which is the same shape the aim has. Ties
 * go to the anchor, as in the close-range scan, since a corner is the more
 * specific thing to have aimed at.
 */
export function nearestAxisFeature(
  outlines: ReadonlyArray<AxisOutline>,
  cursor: { x: number; y: number },
  scope: paper.PaperScope,
): AxisRef | null {
  const cursorPt = new scope.Point(cursor.x, cursor.y);
  let best: { ref: AxisRef; distSq: number } | null = null;
  for (const outline of outlines) {
    if (!outline.pathData) continue;
    let cp: paper.CompoundPath | null = null;
    try {
      cp = new scope.CompoundPath({
        pathData: outline.pathData,
        insert: false,
      });
    } catch {
      continue;
    }
    const subs = subpathsOf(cp);
    for (let pi = 0; pi < subs.length; pi++) {
      const sub = subs[pi]!;
      const segs = sub.segments;
      for (let si = 0; si < segs.length; si++) {
        const seg = segs[si]!;
        const dx = seg.point.x - cursor.x;
        const dy = seg.point.y - cursor.y;
        const dSq = dx * dx + dy * dy;
        // `<=` so an anchor takes a tie from a segment whose nearest point IS
        // that anchor — pointing at a corner should mean the corner.
        if (!best || dSq <= best.distSq) {
          best = {
            ref: {
              kind: "point",
              key: outline.key,
              pathIndex: pi,
              segmentIndex: si,
            },
            distSq: dSq,
          };
        }
      }
      for (let ci = 0; ci < sub.curves.length; ci++) {
        const curve = sub.curves[ci]!;
        if (!curve.isStraight()) continue;
        const loc = curve.getNearestLocation(cursorPt);
        if (!loc) continue;
        const dx = loc.point.x - cursor.x;
        const dy = loc.point.y - cursor.y;
        const dSq = dx * dx + dy * dy;
        if (!best || dSq < best.distSq) {
          best = {
            ref: {
              kind: "segment",
              key: outline.key,
              pathIndex: pi,
              segmentIndex: ci,
            },
            distSq: dSq,
          };
        }
      }
    }
    cp.remove();
  }
  return best?.ref ?? null;
}

/** The axis line's angle in degrees (y-down), or 0 for a point inversion.
 *  Used to express an arbitrary-axis reflection as rotate → axis-aligned flip
 *  → rotate back. */
export function axisAngleDeg(axis: AxisSpec): number {
  if (axis.kind === "point" || !axis.dir) return 0;
  return (Math.atan2(axis.dir.y, axis.dir.x) * 180) / Math.PI;
}

/**
 * Distance from `cursor` to the axis — perpendicular to a segment axis, radial
 * to a point pivot.
 *
 * This is the self-axis GAP: mirroring an object across its OWN edge lands the
 * copy flush against it, and pushing it out by exactly this puts the copy's
 * leading edge under the cursor. One rule covers flush (rest on the edge) and
 * any separation (pull away), so the spacing is something you aim rather than
 * nudge afterwards.
 *
 * Shared by the leather Mirror sub-tool (#973) and the construction one (#343
 * follow-up) — the gesture means the same thing in both, so it must not be
 * implemented twice.
 */
export function axisDistanceMm(
  axis: AxisSpec,
  cursor: { x: number; y: number },
): number {
  const dx = cursor.x - axis.origin.x;
  const dy = cursor.y - axis.origin.y;
  if (axis.kind === "point" || !axis.dir) return Math.hypot(dx, dy);
  const len = Math.hypot(axis.dir.x, axis.dir.y);
  if (!Number.isFinite(len) || len < 1e-9) return Math.hypot(dx, dy);
  // |cross product| with the unit direction = distance to the infinite line.
  return Math.abs(dx * (axis.dir.y / len) - dy * (axis.dir.x / len));
}

/**
 * The translation that pushes a self-axis copy `gapMm` clear of its original,
 * given the source's centre.
 *
 * The reflection moves that centre PERPENDICULAR to a segment axis (and
 * straight through the pivot for a point axis), so the displacement it produces
 * is exactly the direction to push further along — no separate normal to derive
 * and get the sign of wrong.
 */
export function selfAxisGap(
  axis: AxisSpec,
  sourceCenter: { x: number; y: number },
  gapMm: number,
): { dx: number; dy: number } {
  const moved = applyMirror(buildMirrorMatrix(axis), sourceCenter);
  const dx = moved.x - sourceCenter.x;
  const dy = moved.y - sourceCenter.y;
  const len = Math.hypot(dx, dy);
  // A centre sitting ON the axis barely moves under the reflection, so there is
  // no meaningful "away" — leave it rather than pushing in an arbitrary
  // direction.
  if (!Number.isFinite(len) || len < 1e-9) return { dx: 0, dy: 0 };
  return { dx: (dx / len) * gapMm, dy: (dy / len) * gapMm };
}

/** How far the gap must move before a hover preview is worth rebuilding.
 *  Sub-tenth-mm changes aren't visible, and re-parsing every source path on
 *  each pixel of pointer travel buys nothing. */
export const GAP_REDRAW_EPS_MM = 0.1;
